<script setup lang="ts">
import { computed } from 'vue';

import HelpCardsCarousel from './HelpCardsCarousel.vue';
import ReturnCard from './returns/ReturnCard.vue';
import NewMemberWelcome from './NewMemberWelcome.vue';
import RewardsCard from './RewardsCard.vue';
import type { ReturnCardModel } from '../../../api/types/ReturnCardModel';

/**
 * The "Help for you" section on the Help Center home. This is the one place
 * that knows about every card type that can live here — returns, rewards and
 * member welcome. Each feature still owns its own card component and data;
 * this only decides which ones render and in what order, and mounts a single
 * HelpCardsCarousel so everything scrolls together rather than as separate
 * rows.
 *
 * Adding a new card type: import its card component, add whatever prop this
 * section needs to know if it should show, add it to cardCount, add a branch
 * to the template, and add its name to the Conman order property.
 *
 * Each card type has its own Conman flag, resolved by the caller before it
 * gets here. Turning one off removes only that card; the carousel still
 * renders whatever is left, and hides itself when nothing is left.
 */
const props = withDefaults(defineProps<{
  returns?: ReturnCardModel[];
  showNewMemberWelcome?: boolean;
  showRewardsCard?: boolean;
  rewardsAmount?: string;
  cardOrder?: string;
  pageName?: string;
}>(), {
  returns: () => [],
  showNewMemberWelcome: false,
  showRewardsCard: false,
  rewardsAmount: '',
  cardOrder: '',
  pageName: undefined,
});

// Order is configuration, not code: it comes from help.for.you.carousel.order
// in Conman as a comma separated list, so it can be changed per environment
// without a release. A name missing from the list means that card type is not
// rendered at all, which doubles as a quick way to pull one type without
// touching its own flag. Unknown names are ignored.
const DEFAULT_CARD_ORDER = 'returns,rewards,member';

const cardOrder = computed(() => (props.cardOrder || DEFAULT_CARD_ORDER)
  .split(',')
  .map((cardType) => cardType.trim().toLowerCase())
  .filter(Boolean));

const showReturns = computed(() => (
  cardOrder.value.includes('returns') && (props.returns?.length ?? 0) > 0
));

const showWelcome = computed(() => (
  cardOrder.value.includes('member') && props.showNewMemberWelcome
));

const showRewards = computed(() => (
  cardOrder.value.includes('rewards') && props.showRewardsCard
));

const cardCount = computed(() => (
  (showReturns.value ? props.returns.length : 0)
  + (showWelcome.value ? 1 : 0)
  + (showRewards.value ? 1 : 0)
));
</script>

<template>
  <HelpCardsCarousel
    :card-count="cardCount"
    title="Help for you"
    section-view-tag="HELP_CENTER_HELP_FOR_YOU_SECTION"
    :page-name="pageName"
  >
    <template
      v-for="cardType in cardOrder"
      :key="cardType"
    >
      <template v-if="cardType === 'returns' && showReturns">
        <ReturnCard
          v-for="returnItem in returns"
          :key="returnItem.key"
          :return-data="returnItem"
          :page-name="pageName"
        />
      </template>

      <NewMemberWelcome
        v-else-if="cardType === 'member' && showWelcome"
        :page-name="pageName"
      />

      <RewardsCard
        v-else-if="cardType === 'rewards' && showRewards"
        :amount="rewardsAmount"
        :page-name="pageName"
      />
    </template>
  </HelpCardsCarousel>
</template>
